// One live surface moves on the compositor; the chart never changes size.
const restingLayouts = new WeakMap();
const activeMovements = new WeakMap();
export function animateAccountOverlay(host, expanded, immediate = false) {
  const panel = host.querySelector('.bottom-section');
  const divider = host.querySelector('.resize-history-position');
  const chart = host.querySelector('.chart-box');
  if (!panel || !divider || !chart) return null;
  const rect = host.getBoundingClientRect();
  const scale = host.offsetWidth ? rect.width / host.offsetWidth : 1;
  const top = node => (node.getBoundingClientRect().top - rect.top) / (scale || 1);
  // A display:none panel has a zero rect, not its resting position. Start/end
  // it at the divider edge so expanding a collapsed panel travels from below.
  const panelTop = panel.getAttribute('aria-hidden') === 'true'
    ? top(divider) + divider.getBoundingClientRect().height / (scale || 1)
    : top(panel);
  const before = [panelTop, top(divider)];
  const previous = activeMovements.get(host);
  if (!immediate && previous?.expanded === expanded) return previous;
  // Capture the on-screen position before removing the previous transform.
  // A quick reversal must continue here, not jump to the previous endpoint.
  previous?.cancel();
  if (expanded) {
    if (restingLayouts.has(host)) return null;
    const hidden = panel.getAttribute('aria-hidden') || 'false';
    const restingPanelTop = hidden === 'true'
      ? top(divider) + divider.getBoundingClientRect().height / (scale || 1)
      : top(panel);
    restingLayouts.set(host, { positions: [restingPanelTop, top(divider)], hidden });
    const toolbar = host.querySelector('#chartcontrols');
    const toolbarBottom = toolbar ? (toolbar.getBoundingClientRect().bottom - rect.top) / (scale || 1) : 40;
    const dividerHeight = divider.getBoundingClientRect().height / (scale || 1);
    host.style.setProperty('--overlay-chart-height', `${chart.clientHeight}px`);
    host.style.setProperty('--overlay-controls-top', `${toolbarBottom}px`);
    host.style.setProperty('--overlay-panel-top', `${toolbarBottom + dividerHeight}px`);
    host.dataset.accountOverlay = 'true';
    panel.setAttribute('aria-hidden', 'false');
  } else if (!restingLayouts.has(host)) return null;
  const animations = [];
  let finished = false, startFrame;
  const finish = () => {
    if (finished) return;
    finished = true;
    cancelAnimationFrame(startFrame);
    if (!expanded) {
      panel.setAttribute('aria-hidden', restingLayouts.get(host)?.hidden || 'false');
      delete host.dataset.accountOverlay;
      ['--overlay-chart-height', '--overlay-controls-top', '--overlay-panel-top'].forEach(key => host.style.removeProperty(key));
      restingLayouts.delete(host);
    }
    animations.forEach(animation => animation.cancel());
    delete host.dataset.accountOverlayAnimating;
    activeMovements.delete(host);
  };
  if (immediate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    finish();
    return null;
  }
  host.dataset.accountOverlayAnimating = 'true';
  [panel, divider].forEach((node, index) => {
    const start = before[index] - top(node);
    const end = expanded ? 0 : restingLayouts.get(host).positions[index] - top(node);
    const animation = node.animate([
      { translate: `0 ${start}px` }, { translate: `0 ${end}px` },
    ], { duration: 320, easing: 'cubic-bezier(.4, 0, .2, 1)', fill: 'both' });
    animation.pause();
    animation.currentTime = 0;
    animations.push(animation);
  });
  // Match chart resizing: paint the starting pose before the shared clock runs.
  startFrame = requestAnimationFrame(() => {
    startFrame = requestAnimationFrame(startTime => {
      animations.forEach(animation => {
        animation.play();
        animation.startTime = startTime;
      });
    });
  });
  // On reset retain the tall panel until movement ends. Shrinking first exposes
  // the background below it. Restore flex sizing and remove transforms together.
  Promise.all(animations.map(animation => animation.finished)).then(finish, finish);
  const movement = { cancel: finish, expanded };
  activeMovements.set(host, movement);
  return movement;
}
